import { MapMarkerVariantIconMarkerType } from '__generated__v2/graphql';
import { isEqual } from 'lodash';
import mapboxgl from 'mapbox-gl';
import { MarkerGlobalSettings } from 'pages/special/OsloDesignFair/map/CtxMapView';
import { VariantAvailableIcons } from 'pages/special/OsloDesignFair/map/mapConstants';
import { VariantSizes } from 'pages/special/OsloDesignFair/map/markers/view/VariantIconMarkers/VariantIconMarker/shared/variantUtils';
import { VariantSwitcher } from 'pages/special/OsloDesignFair/map/markers/view/VariantIconMarkers/VariantSwitcher.tsx';
import React, { memo, useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { MapRef } from 'react-map-gl';
import { useUpdateEffect } from 'react-use';
import styled, { ThemeProvider, useTheme } from 'styled-components';

export interface VariantIconMarkerComponentProps {
    size: VariantSizes;
    mapRef: React.RefObject<MapRef | null>;
    markerType: MapMarkerVariantIconMarkerType;
    color: string;
    sublabel: string | null | undefined;
    customImageUrl: string | null;
    imageUrl: string | null;
    imageShowBg: boolean | null | undefined;
    imageShowContain: boolean | null | undefined;
    iconName: VariantAvailableIcons;
    eventShowImage: boolean | null | undefined;
    lat: number;
    lng: number;
    eventDate: string | null;
    language: string;
    markerId: string;
    highlighted: boolean;
    text: string;
    className?: string;
    isWide: boolean;
    isEditMode: boolean;
    onClick?: (markerId: string) => void;
    selectable: boolean;
    markerGlobalSettings: MarkerGlobalSettings['variant_icon'];
    isActive: boolean;
    forceHighlightSelectableMarkers: boolean;
}

const VariantIconMarkerComponent = (props: VariantIconMarkerComponentProps) => {
    const { markerId, size, isWide } = props;

    const isActive = props.isActive || props.forceHighlightSelectableMarkers;

    return (
        <S_VariantIconMarker
            className={props.className}
            $isActive={isActive}
            $isWide={isWide}
            style={{
                zIndex: size
            }}
            // aria-label is required for moving the marker in real-time in the editor
            aria-label={markerId}
            onClick={(e) => {
                if (!props.selectable) {
                    e.stopPropagation();
                    return;
                }
                props.onClick?.(markerId);
                e.stopPropagation();
            }}
        >
            <S_MarkerChild>
                <VariantSwitcher variantIconMarkerProps={props} />
            </S_MarkerChild>
        </S_VariantIconMarker>
    );
};

const S_VariantIconMarker = styled.div<{ $isWide: boolean; $isActive: boolean }>`
    display: flex;
    flex-direction: column;
    position: relative;
    justify-content: center;
    align-items: center;
    cursor: ${(props) => (props.$isActive ? 'auto' : 'pointer')};
    pointer-events: ${(props) => (props.$isActive && props.$isWide ? 'none' : 'auto')};
`;

const VariantIconMarkerMemoed = memo(VariantIconMarkerComponent, (prev, next) => {
    if (prev.isEditMode) {
        return isEqual(prev, next);
    }
    return (
        prev.isActive === next.isActive &&
        prev.size === next.size &&
        prev.forceHighlightSelectableMarkers === next.forceHighlightSelectableMarkers
    );
});

export const VariantIconMarker = (props: VariantIconMarkerComponentProps) => {
    const rootRef = useRef<Root | null>(null);
    const nativeMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const markerRef = useRef<HTMLElement | null>(null);
    const theme = useTheme();

    const markerEl = <VariantIconMarkerMemoed {...props} />;

    useUpdateEffect(() => {
        nativeMarkerRef?.current?.setLngLat([props.lng, props.lat]);
    }, [props.lat, props.lng]);

    useEffect(() => {
        if (!props.mapRef.current) {
            return;
        }

        const divElement = document.createElement('div');
        rootRef.current = createRoot(divElement);
        rootRef.current.render(<ThemeProvider theme={theme}>{markerEl}</ThemeProvider>);

        const marker = new mapboxgl.Marker({
            element: divElement,
            className: 'zIndex' + (props.isActive ? 'Active' : props.size)
        })
            .setLngLat([props.lng, props.lat])
            .addTo(props.mapRef.current?.getMap());

        nativeMarkerRef.current = marker;
        markerRef.current = marker.getElement();

        return () => {
            marker.remove();
        };
    }, [props.mapRef.current]);

    useUpdateEffect(() => {
        if (markerRef?.current?.className) {
            markerRef.current.classList.remove('zIndex1', 'zIndex2', 'zIndex3', 'zIndexActive');
            markerRef.current.classList.add('zIndex' + props.size);
            if (props.isActive && !props.forceHighlightSelectableMarkers) {
                markerRef.current.classList.add('zIndexActive');
            }
        }
    }, [props.isActive, props.size, props.forceHighlightSelectableMarkers]);

    useUpdateEffect(() => {
        rootRef.current?.render(<ThemeProvider theme={theme}>{markerEl}</ThemeProvider>);
    }, [markerEl]);

    return <></>;
};

const S_MarkerChild = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;
